import _ from 'lodash';
import Pusher, { Channel } from 'pusher-js';

export default class PusherState {
  private readonly _pusherKey: string;
  private _socket: Pusher | null;
  private _token: string | null;
  // Channel names with an active (subscribed but not yet unsubscribed)
  // subscription. Used to decide whether it's safe to tear down the shared
  // socket: this socket is shared across every marketplace Pusher consumer
  // (order status, kiosk InstoreCart, aerosync pre-auth,
  // use-public-context-session-pusher), and none of them re-subscribe on
  // their own after a token change, so disconnecting it out from under an
  // active subscriber would silently kill their real-time updates (and any
  // later unsubscribe() call against the now-null socket would throw).
  private readonly _activeChannelNames: Set<string>;
  // Set when updateToken() is called while channels are still active, so we
  // know to apply the deferred socket teardown once the last one unsubscribes
  // (rather than tearing the socket down on every ordinary "last unsubscribe",
  // which would add reconnect churn for no reason).
  private _pendingTokenChange: boolean;

  constructor(pusherKey: string, token?: string | null) {
    this._pusherKey = pusherKey;
    this._socket = null;
    this._token = token ?? null;
    this._activeChannelNames = new Set();
    this._pendingTokenChange = false;
  }

  // Called whenever the consumer's auth token changes (login/logout) so that
  // *subsequent* subscriptions to private channels (e.g. private-order-<id>)
  // authenticate as the current user. Pusher-js has no API to reconfigure
  // auth on an already-connected socket, so applying a new token requires a
  // fresh socket -- but if anything is actively subscribed right now, tearing
  // the socket down would silently kill those live subscriptions (nothing
  // re-subscribes on its own). So we only disconnect immediately when no
  // channel is active; otherwise we defer the teardown until the last active
  // channel is unsubscribed (see unsubscribe() below).
  //
  // Known narrow window: while any channel remains active after a token
  // change (e.g. logout), the deferred socket keeps authenticating with the
  // *previous* token until the last active channel unsubscribes -- so a brand
  // new subscribe() made during that window still rides the old token rather
  // than immediately picking up the new one. This self-corrects as soon as
  // the active channel count reaches zero, and it only ever reuses a token
  // that legitimately belonged to the same browser session (no privilege
  // escalation to a different user's data) -- but it does mean a logged-out
  // session's token isn't guaranteed to be unusable for new subscriptions
  // the instant logout() is called.
  updateToken = (token: string | null): void => {
    this._token = token;

    if (this._activeChannelNames.size === 0) {
      this._teardownSocket();
      return;
    }

    this._pendingTokenChange = true;
  };

  private readonly _teardownSocket = (): void => {
    if (this._socket) {
      this._socket.disconnect();
      this._socket = null;
    }
    this._pendingTokenChange = false;
  };

  private readonly _getSocketLazy = (): Pusher => {
    if (this._socket) {
      return this._socket;
    }

    return (this._socket = new Pusher(this._pusherKey, {
      cluster: `us3`,
      forceTLS: true,
      ...(this._token
        ? {
            auth: { headers: { 'user-token': this._token } },
            authEndpoint: '/api/v2/pusher/authenticate_user',
          }
        : {}),
    }));
  };

  subscribe = (channelName: string): Channel => {
    const socket = this._getSocketLazy();
    const channel = socket.subscribe(channelName);
    this._activeChannelNames.add(channelName);

    return channel;
  };

  unsubscribe = (channelName: string): void => {
    this._activeChannelNames.delete(channelName);

    if (!_.isEmpty(this._socket)) {
      if (_.isEmpty(this._socket.channels.channels[channelName])) {
        // This is throwing erroneously due to react 18 double renders
        // throw new Error(`PusherState: The channel '${channelName}' doesn't exist, did you forget to call subscribe??`);
      } else {
        this._socket.unsubscribe(channelName);
      }
    }

    // A token change arrived while this (or another) channel was still
    // active; now that nothing is subscribed, it's safe to apply it by
    // tearing down the socket so the next subscribe() lazily creates a fresh
    // one with the current token. This must run regardless of which branch
    // above was taken (including the react-18-double-invoke early return, and
    // even if _socket was already null) -- otherwise a pending token change
    // can get stranded and never applied.
    if (this._activeChannelNames.size === 0 && this._pendingTokenChange) {
      this._teardownSocket();
    }
  };
}
